/**
 * Mandant-Regelwerk — Frontend-Sicht (T04, 2026-07-07).
 *
 * Die frühere dritte KOPIE des Regelwerks (SIBIT_DEFAULT etc.) ist GELÖSCHT:
 * sie war gegenüber worker/hub gedriftet (pre-v1.6.0) und fiel für unbekannte
 * Mandanten STILL auf Sibit-Regeln zurück. Das Frontend holt das Regelwerk
 * jetzt ausschließlich über GET /api/client-rules — der Hub antwortet aus
 * derselben loadClientRuleset-Funktion, mit der der Worker rechnet
 * (Mirror-Paar worker⇄hub, erzwungen durch scripts/check-rules-mirror.sh).
 * Anzeige == Rechnung, fail-loud statt Fallback.
 *
 * Hier leben nur noch: die TYPEN (Strukturvertrag der API) + der React-Hook.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

export type UeberstundenPayMode = "cash" | "za" | "pauschale";

export interface ClientRule {
  label: string;
  description: string;
  implementation?: string;
}

export interface ClientRuleset {
  client_id: string;
  client_name: string;
  effective_from: string;
  effective_until?: string | null;
  ueberstunden_weekly_threshold_h: number;
  week_boundary: "monday_to_sunday" | "iso";
  threshold_model: "same_for_all" | "per_soll" | "fulltime_soll";
  status_days_count_to_weekly: boolean;
  ueberstunden_100_enabled: boolean;
  mehrstunden_routing: "always_za" | "always_cash" | "per_employee";
  default_ueberstunden_pay_mode: UeberstundenPayMode;
  daily_ueberstunden_threshold_h: number;
  ueberstunden_combine: "max" | "sum";
  ueberstunden_offsettable_by_minus: boolean;
  za_konto_mode: "saldo" | "gutstunden";
  soll_credit_status_codes: string[];
  rules: ClientRule[];
  rzl_export_labels: {
    cash: { mehrstunden: string; ueberstunden_50: string };
    za: { mehrstunden: string; ueberstunden_50: string };
    pauschale: { mehrstunden: string; ueberstunden_50: string };
  };
}

export interface ClientRulesetState {
  /** undefined = lädt · null = kein Regelwerk (fail-loud, 404) · sonst geladen */
  ruleset: ClientRuleset | null | undefined;
  /** true wenn eine client_rules-DB-Row existiert (false = reiner Code-Default). */
  hasDbRow: boolean;
  error: string | null;
}

/** Lädt das effektive Regelwerk des Mandanten vom Hub. */
export function useClientRuleset(clientId: string | null | undefined): ClientRulesetState {
  const [state, setState] = useState<ClientRulesetState>({
    ruleset: undefined, hasDbRow: false, error: null,
  });

  useEffect(() => {
    let alive = true;
    setState({ ruleset: undefined, hasDbRow: false, error: null });
    api.clientRules(clientId)
      .then((res) => {
        if (!alive) return;
        if (res === null) setState({ ruleset: null, hasDbRow: false, error: null });
        else setState({ ruleset: res.ruleset, hasDbRow: res.has_db_row, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        setState({ ruleset: null, hasDbRow: false, error: e instanceof Error ? e.message : String(e) });
      });
    return () => { alive = false; };
  }, [clientId]);

  return state;
}
